import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import { organisationApi } from '@/api/organisation';
import { workflowsApi, type WorkflowDraftPayload } from '@/api/workflows';
import type { RoleDefinition } from '@/types';
import type { InstitutionPositionView } from '@/types/organisation';
import type {
  ApproverType,
  ConditionOp,
  WorkflowDefinition,
  WorkflowHistoryEvent,
  WorkflowProcess,
  WorkflowRules,
  WorkflowStep,
  WorkflowValidationReport,
} from '@/types/workflows';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { StateBadge } from '@/components/platform/StateBadge';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { Icon } from '@/components/icons';
import { formatDateTime } from '@/utils/format';
import { WorkflowChain, type ChainStepView } from './WorkflowChain';
import { validateDraft } from './workflow-validation';
import { refLabels, type ApproverOption } from './approver-labels';
import styles from './WorkflowDesignerPage.module.css';

/**
 * The workflow designer — one process, its versions, and the step chain being
 * written for it.
 *
 * THE SCREEN IS SPLIT THE WAY THE WORK IS. Left, the builder: step cards edited
 * in place, rules underneath. Right, the live chain — the SAME diagram the
 * instance detail will later render for real cases — redrawn on every edit so
 * the author is always looking at what approvers will live through, plus the
 * validation verdicts. Right rail: the version history, DRAFT/ACTIVE/SUPERSEDED.
 *
 * ONLY LEGAL MOVES ARE OFFERED. The server owns the definition lifecycle; this
 * page never renders a button the machine would refuse: drafts can be saved,
 * validated, activated; ACTIVE is read-only with "new version"; SUPERSEDED is
 * history. When the server does refuse (a 409 on a stale draft, a 403), its
 * sentence is shown verbatim — the machine explains itself better than a
 * client-side paraphrase would.
 *
 * TWO VALIDATORS, ONE TRUTH. workflow-validation.ts mirrors the server's checks
 * live, per keystroke, so problems surface while the author is still in the
 * step card. The server's own report renders verbatim after validate/activate.
 * The mirror advises; the server rules.
 */

const NS = 'admin.workflows';

const APPROVER_TYPES: readonly ApproverType[] = ['ROLE', 'ANY_OF_ROLES', 'POSITION'];
const CONDITION_OPS: readonly ConditionOp[] = ['GT', 'GTE', 'LT', 'EQ', 'IN'];

/** The condition row as edited: text until the moment it is sent. */
interface ConditionDraft {
  enabled: boolean;
  field: string;
  op: ConditionOp;
  value: string;
}

interface EditorState {
  /** null while the draft only exists in the browser; set after first save. */
  definitionId: string | null;
  title: string;
  description: string;
  steps: WorkflowStep[];
  initiatorCannotApprove: boolean;
  distinctApproversAcrossSteps: boolean;
  condition: ConditionDraft;
  dirty: boolean;
}

function blankStep(stepNo: number): WorkflowStep {
  return {
    stepNo,
    title: '',
    approverType: 'ROLE',
    approverRefs: [],
    quorum: 1,
    allowSelfApproval: false,
    timeoutHours: null,
    escalateTo: null,
  };
}

function conditionDraftFrom(rules: WorkflowRules): ConditionDraft {
  if (!rules.condition) return { enabled: false, field: '', op: 'GTE', value: '' };
  const value = rules.condition.value;
  return {
    enabled: true,
    field: rules.condition.field,
    op: rules.condition.op,
    value: Array.isArray(value) ? value.join(', ') : String(value),
  };
}

/** A numeric string is sent as a number so `amount GTE 100000` compares as money,
 *  not as text; IN splits on commas. Everything else rides as the typed string. */
function conditionValueFrom(draft: ConditionDraft): string | number | string[] {
  if (draft.op === 'IN') {
    return draft.value
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
  }
  const trimmed = draft.value.trim();
  return /^-?\d+(\.\d+)?$/.test(trimmed) ? Number(trimmed) : trimmed;
}

function rulesFrom(editor: EditorState): WorkflowRules {
  return {
    initiatorCannotApprove: editor.initiatorCannotApprove,
    distinctApproversAcrossSteps: editor.distinctApproversAcrossSteps,
    condition: editor.condition.enabled
      ? { field: editor.condition.field.trim(), op: editor.condition.op, value: conditionValueFrom(editor.condition) }
      : null,
  };
}

/** What actually goes over the wire: steps renumbered to their positions, the
 *  escalation dropped whenever its timeout is gone. */
function payloadFrom(editor: EditorState): WorkflowDraftPayload {
  return {
    title: editor.title,
    description: editor.description,
    steps: editor.steps.map((step, index) => ({
      ...step,
      stepNo: index + 1,
      escalateTo: step.timeoutHours === null ? null : step.escalateTo,
    })),
    rules: rulesFrom(editor),
  };
}

function editorFrom(definition: WorkflowDefinition): EditorState {
  return {
    definitionId: definition.id,
    title: definition.title,
    description: definition.description,
    steps: definition.steps.map((step) => ({ ...step, approverRefs: [...step.approverRefs] })),
    initiatorCannotApprove: definition.rules.initiatorCannotApprove,
    distinctApproversAcrossSteps: definition.rules.distinctApproversAcrossSteps,
    condition: conditionDraftFrom(definition.rules),
    dirty: false,
  };
}

/** A fresh draft: seeded from the version it will replace when there is one,
 *  otherwise a single blank step — never zero, the mirror would only nag. */
function newEditor(seed: WorkflowDefinition | null, fallbackTitle: string): EditorState {
  if (seed) {
    return { ...editorFrom(seed), definitionId: null, dirty: true };
  }
  return {
    definitionId: null,
    title: fallbackTitle,
    description: '',
    steps: [blankStep(1)],
    initiatorCannotApprove: true,
    distinctApproversAcrossSteps: false,
    condition: { enabled: false, field: '', op: 'GTE', value: '' },
    dirty: true,
  };
}

export function WorkflowDesignerPage() {
  const { t, i18n } = useTranslation();
  const { processKey = '' } = useParams();
  const { activeContext } = useAppContext();
  const toast = useToast();
  const institutionId = activeContext?.institution?.id ?? '';
  const locale = (i18n.language || 'en').split('-')[0];

  const { state: processesState } = useAsyncData<WorkflowProcess[]>(
    `wf-processes-${institutionId}`,
    () => workflowsApi.admin.processes(institutionId),
    !!institutionId,
  );

  const { state: versionsState, reload: reloadVersions } = useAsyncData<WorkflowDefinition[]>(
    `wf-versions-${institutionId}-${processKey}`,
    () => workflowsApi.admin.versions(institutionId, processKey),
    !!institutionId && !!processKey,
  );

  /* Approver directories, LIVE: the role catalogue this institution can staff
   * (custom roles included) and its position register. The designer offers only
   * what exists — a typo cannot become an approver. */
  const { state: rolesState } = useAsyncData<RoleDefinition[]>(
    `wf-roles-${institutionId}`,
    () => api.admin.roles.listDefinitions(institutionId),
    !!institutionId,
  );

  const { state: positionsState } = useAsyncData<InstitutionPositionView[]>(
    `wf-positions-${institutionId}-${locale}`,
    () => organisationApi.positions.forInstitution(institutionId, locale),
    !!institutionId,
  );

  const roleOptions: ApproverOption[] = useMemo(
    () =>
      rolesState.status === 'ready'
        ? rolesState.data.map((role) => ({ value: role.code, label: role.name || role.code }))
        : [],
    [rolesState],
  );

  const positionOptions: ApproverOption[] = useMemo(
    () =>
      positionsState.status === 'ready'
        ? positionsState.data
            .filter((position) => position.active)
            .map((position) => ({ value: position.canonicalCode, label: position.displayTitle }))
        : [],
    [positionsState],
  );

  const process =
    processesState.status === 'ready'
      ? (processesState.data.find((entry) => entry.key === processKey) ?? null)
      : null;

  const versions = versionsState.status === 'ready' ? versionsState.data : [];
  const draft = versions.find((entry) => entry.status === 'DRAFT') ?? null;
  const activeVersion = versions.find((entry) => entry.status === 'ACTIVE') ?? null;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected =
    versions.find((entry) => entry.id === selectedId) ?? draft ?? activeVersion ?? versions[0] ?? null;

  const [editor, setEditor] = useState<EditorState | null>(null);
  const [serverReport, setServerReport] = useState<WorkflowValidationReport | null>(null);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [activateOpen, setActivateOpen] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [historyFor, setHistoryFor] = useState<WorkflowDefinition | null>(null);

  /* The one automatic seeding: when the page lands on a process that already
   * has a draft, the builder opens on it. Everything else — new version,
   * customise — seeds explicitly from a button, so an edit in progress is never
   * silently replaced by a refetch. */
  useEffect(() => {
    if (editor === null && draft) setEditor(editorFrom(draft));
    // Editing a definition that a reload proved is no longer a draft
    // (activated in another tab) must not keep a stale editor alive.
    if (editor !== null && editor.definitionId && !versions.some((v) => v.id === editor.definitionId && v.status === 'DRAFT')) {
      setEditor(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the data, not the editor identity
  }, [versionsState]);

  const editingSelected = editor !== null && (editor.definitionId === null || editor.definitionId === selected?.id);
  const showEditor = editor !== null && (editor.definitionId === null || editingSelected);

  const optionsForType = (type: ApproverType): ApproverOption[] =>
    type === 'POSITION' ? positionOptions : roleOptions;

  const mirrorIssues = editor ? validateDraft(payloadFrom(editor).steps, rulesFrom(editor)) : [];

  const chainSteps: ChainStepView[] = (editor ? payloadFrom(editor).steps : (selected?.steps ?? [])).map(
    (step) => ({
      title: step.title,
      approverSummary: summarizeApprovers(step, optionsForType(step.approverType), t),
      status: 'pending' as const,
    }),
  );

  const patchEditor = (patch: Partial<EditorState>) =>
    setEditor((current) => (current ? { ...current, ...patch, dirty: true } : current));

  const patchStep = (index: number, patch: Partial<WorkflowStep>) =>
    setEditor((current) => {
      if (!current) return current;
      const steps = current.steps.map((step, at) => (at === index ? { ...step, ...patch } : step));
      return { ...current, steps, dirty: true };
    });

  const moveStep = (index: number, delta: -1 | 1) =>
    setEditor((current) => {
      if (!current) return current;
      const target = index + delta;
      if (target < 0 || target >= current.steps.length) return current;
      const steps = [...current.steps];
      [steps[index], steps[target]] = [steps[target], steps[index]];
      return { ...current, steps, dirty: true };
    });

  const removeStep = (index: number) =>
    setEditor((current) =>
      current ? { ...current, steps: current.steps.filter((_, at) => at !== index), dirty: true } : current,
    );

  const addStep = () =>
    setEditor((current) =>
      current ? { ...current, steps: [...current.steps, blankStep(current.steps.length + 1)], dirty: true } : current,
    );

  const saveDraft = async () => {
    if (!editor) return;
    setSaving(true);
    setActionError(null);
    try {
      const payload = payloadFrom(editor);
      const saved = editor.definitionId
        ? await workflowsApi.admin.updateDraft(institutionId, editor.definitionId, payload)
        : await workflowsApi.admin.createDraft(institutionId, processKey, payload);
      toast.success(t(`${NS}.designer.draftSaved`));
      setEditor({ ...editorFrom(saved), dirty: false });
      setSelectedId(saved.id);
      setServerReport(null);
      reloadVersions();
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const runValidate = async () => {
    if (!editor?.definitionId) return;
    setValidating(true);
    setActionError(null);
    try {
      setServerReport(await workflowsApi.admin.validate(institutionId, editor.definitionId));
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : t('common.actionFailed'));
    } finally {
      setValidating(false);
    }
  };

  const activate = async (reason: string) => {
    if (!editor?.definitionId) return;
    setActivating(true);
    setActivateError(null);
    try {
      await workflowsApi.admin.transition(institutionId, editor.definitionId, 'ACTIVE', reason);
      toast.success(t(`${NS}.designer.activated`));
      setActivateOpen(false);
      setEditor(null);
      setServerReport(null);
      setSelectedId(null);
      reloadVersions();
    } catch (cause) {
      // The machine's refusal, verbatim — a 409 names the exact conflict.
      setActivateError(cause instanceof Error ? cause.message : t('common.actionFailed'));
    } finally {
      setActivating(false);
    }
  };

  const startNewVersion = () => {
    setEditor(newEditor(activeVersion ?? selected, process?.title ?? processKey));
    setServerReport(null);
    setActionError(null);
  };

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb}>
        <Link to="/admin/institution/workflows" className={styles.backLink}>
          <Icon name="chevronLeft" size={14} />
          {t(`${NS}.designer.back`)}
        </Link>
      </nav>

      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.headerName}>{process?.title ?? processKey}</h1>
          {process?.description && <p className={styles.headerSubtitle}>{process.description}</p>}
        </div>
        <div className={styles.headerState}>
          {showEditor ? (
            <StateBadge kind="lifecycle" value="DRAFT" />
          ) : (
            selected && <StateBadge kind="lifecycle" value={selected.status} />
          )}
        </div>
        <div className={styles.headerActions}>
          {showEditor && editor && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={saveDraft}
                loading={saving}
                loadingLabel={t('common.submitting')}
                disabled={!editor.dirty && editor.definitionId !== null}
              >
                {t(`${NS}.designer.saveDraft`)}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={runValidate}
                loading={validating}
                loadingLabel={t('common.submitting')}
                // Validation runs against the SAVED draft; an unsaved edit would
                // make the verdict describe a document nobody can see.
                disabled={editor.definitionId === null || editor.dirty}
                title={editor.definitionId === null || editor.dirty ? t(`${NS}.designer.saveFirst`) : undefined}
              >
                {t(`${NS}.designer.validate`)}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setActivateError(null);
                  setActivateOpen(true);
                }}
                disabled={editor.definitionId === null || editor.dirty || mirrorIssues.length > 0}
                title={
                  editor.definitionId === null || editor.dirty
                    ? t(`${NS}.designer.saveFirst`)
                    : mirrorIssues.length > 0
                      ? t(`${NS}.designer.fixIssuesFirst`)
                      : undefined
                }
              >
                {t(`${NS}.designer.activate`)}
              </Button>
            </>
          )}
          {!showEditor && selected?.status === 'ACTIVE' && !draft && (
            <Button size="sm" onClick={startNewVersion}>
              {t(`${NS}.designer.newVersion`)}
            </Button>
          )}
        </div>
      </header>

      {actionError && (
        <p className={styles.actionError} role="alert">
          {actionError}
        </p>
      )}

      <AsyncPanel state={versionsState} onRetry={reloadVersions}>
        {() => (
          <div className={styles.layout}>
            <div className={styles.mainColumn}>
              {showEditor && editor ? (
                <>
                  <Card className={styles.metaCard}>
                    <div className={styles.metaFields}>
                      <label className={styles.fieldLabel}>
                        {t(`${NS}.designer.titleLabel`)}
                        <input
                          className={styles.textInput}
                          value={editor.title}
                          onChange={(event) => patchEditor({ title: event.target.value })}
                        />
                      </label>
                      <label className={styles.fieldLabel}>
                        {t(`${NS}.designer.descriptionLabel`)}
                        <input
                          className={styles.textInput}
                          value={editor.description}
                          onChange={(event) => patchEditor({ description: event.target.value })}
                        />
                      </label>
                    </div>
                  </Card>

                  <section aria-label={t(`${NS}.steps.title`)}>
                    <h2 className={styles.sectionTitle}>{t(`${NS}.steps.title`)}</h2>
                    <ol className={styles.stepList}>
                      {editor.steps.map((step, index) => (
                        <li key={index} className={styles.stepListItem}>
                          <StepCard
                            step={step}
                            index={index}
                            count={editor.steps.length}
                            options={optionsForType(step.approverType)}
                            onPatch={(patch) => patchStep(index, patch)}
                            onMove={(delta) => moveStep(index, delta)}
                            onRemove={() => removeStep(index)}
                          />
                        </li>
                      ))}
                    </ol>
                    <Button variant="secondary" size="sm" onClick={addStep} leadingIcon={<Icon name="plus" size={14} />}>
                      {t(`${NS}.steps.add`)}
                    </Button>
                  </section>

                  <RulesPanel editor={editor} onPatch={patchEditor} />
                </>
              ) : selected ? (
                <ReadOnlyDefinition definition={selected} locale={locale} />
              ) : (
                <Card className={styles.noDefinition}>
                  <p className={styles.noDefinitionTitle}>{t(`${NS}.designer.noDefinition`)}</p>
                  <p className={styles.noDefinitionBody}>
                    {process?.platformDefault
                      ? t(`${NS}.designer.noDefinitionDefault`, { version: process.platformDefault.version })
                      : t(`${NS}.designer.noDefinitionNone`)}
                  </p>
                  <Button size="sm" onClick={startNewVersion}>
                    {t(`${NS}.designer.createDraft`)}
                  </Button>
                </Card>
              )}
            </div>

            <aside className={styles.sideColumn}>
              <Card className={styles.chainCard}>
                <h2 className={styles.sectionTitle}>{t(`${NS}.designer.chainTitle`)}</h2>
                <WorkflowChain
                  steps={chainSteps}
                  makerLabel={t(`${NS}.chain.makerLabel`)}
                  approvedLabel={t(`${NS}.chain.approvedLabel`)}
                  rejectedLabel={t(`${NS}.chain.rejectedLabel`)}
                  ariaLabel={t(`${NS}.chain.designAria`, { n: chainSteps.length })}
                />
              </Card>

              {showEditor && (
                <Card className={styles.validationCard}>
                  <h2 className={styles.sectionTitle}>{t(`${NS}.validation.title`)}</h2>
                  {mirrorIssues.length === 0 ? (
                    <p className={styles.validationOk}>
                      <Icon name="check" size={14} />
                      {t(`${NS}.validation.ok`)}
                    </p>
                  ) : (
                    <ul className={styles.validationList}>
                      {mirrorIssues.map((issue, index) => (
                        <li key={`${issue.code}-${issue.stepNo ?? 'w'}-${index}`} className={styles.validationItem}>
                          {issue.stepNo !== undefined && (
                            <span className={styles.validationStep}>
                              {t(`${NS}.validation.stepPrefix`, { no: issue.stepNo })}
                            </span>
                          )}
                          {t(`${NS}.validation.${issue.code}`)}
                        </li>
                      ))}
                    </ul>
                  )}
                  {serverReport && (
                    <div className={styles.serverReport}>
                      <h3 className={styles.serverReportTitle}>{t(`${NS}.serverReport.title`)}</h3>
                      {serverReport.valid ? (
                        <p className={styles.validationOk}>
                          <Icon name="check" size={14} />
                          {t(`${NS}.serverReport.valid`)}
                        </p>
                      ) : (
                        <ul className={styles.validationList}>
                          {/* The server's own sentences, untranslated and
                              unparaphrased — this is the ruling. */}
                          {serverReport.errors.map((error, index) => (
                            <li key={`${error.code ?? 'err'}-${index}`} className={styles.validationItem}>
                              {error.message}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </Card>
              )}

              <Card className={styles.versionsCard}>
                <h2 className={styles.sectionTitle}>{t(`${NS}.designer.versionsTitle`)}</h2>
                {versions.length === 0 ? (
                  <p className={styles.versionsEmpty}>{t(`${NS}.designer.versionsEmpty`)}</p>
                ) : (
                  <ul className={styles.versionList}>
                    {versions.map((version) => (
                      <li key={version.id}>
                        <button
                          type="button"
                          className={
                            version.id === selected?.id
                              ? `${styles.versionRow} ${styles.versionRowSelected}`
                              : styles.versionRow
                          }
                          onClick={() => {
                            setSelectedId(version.id);
                            if (version.status === 'DRAFT') setEditor(editorFrom(version));
                          }}
                        >
                          <span className={styles.versionLabel}>
                            {t(`${NS}.designer.versionLabel`, { version: version.version })}
                          </span>
                          <StateBadge kind="lifecycle" value={version.status} size="sm" />
                          <span className={styles.versionDate}>{formatDateTime(version.createdAt, locale)}</span>
                        </button>
                        <button
                          type="button"
                          className={styles.historyLink}
                          onClick={() => setHistoryFor(version)}
                        >
                          {t(`${NS}.designer.history`)}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </aside>
          </div>
        )}
      </AsyncPanel>

      <ConfirmActionDialog
        open={activateOpen}
        title={t(`${NS}.designer.activateTitle`)}
        intro={t(`${NS}.designer.activateIntro`, { process: process?.title ?? processKey })}
        tone="warning"
        impactStatus="ready"
        impact={
          <p className={styles.activateImpact}>
            {t(`${NS}.designer.activateImpact`, { n: editor?.steps.length ?? 0 })}
          </p>
        }
        confirmLabel={t(`${NS}.designer.activate`)}
        submitting={activating}
        error={activateError}
        onCancel={() => setActivateOpen(false)}
        onConfirm={activate}
      />

      <HistoryModal
        definition={historyFor}
        institutionId={institutionId}
        locale={locale}
        onClose={() => setHistoryFor(null)}
      />
    </div>
  );
}

function summarizeApprovers(
  step: WorkflowStep,
  options: readonly ApproverOption[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const labels = refLabels(step.approverRefs, options);
  const who = labels.join(', ');
  if (step.approverRefs.length === 0) return t(`${NS}.chain.unknownApprover`);
  if (step.approverRefs.length <= 1 && step.quorum <= 1) return t(`${NS}.chain.single`, { who });
  return t(`${NS}.chain.anyOf`, { quorum: step.quorum, who });
}

/* ─── One step card ─── */

function StepCard({
  step,
  index,
  count,
  options,
  onPatch,
  onMove,
  onRemove,
}: {
  step: WorkflowStep;
  index: number;
  count: number;
  options: ApproverOption[];
  onPatch: (patch: Partial<WorkflowStep>) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const stepNo = index + 1;

  const available = options.filter((option) => !step.approverRefs.includes(option.value));

  const setRefs = (refs: string[]) =>
    // The quorum follows the refs down so removing an approver can never leave
    // a quorum the remaining list cannot meet.
    onPatch({ approverRefs: refs, quorum: Math.min(Math.max(step.quorum, 1), Math.max(refs.length, 1)) });

  return (
    <Card className={styles.stepCard}>
      <div className={styles.stepCardHead}>
        <span className={styles.stepCardNo}>{stepNo}</span>
        <input
          className={`${styles.textInput} ${styles.stepTitleInput}`}
          value={step.title}
          placeholder={t(`${NS}.steps.titlePlaceholder`)}
          aria-label={t(`${NS}.steps.stepTitleLabel`, { no: stepNo })}
          onChange={(event) => onPatch({ title: event.target.value })}
        />
        <div className={styles.stepCardTools}>
          <button
            type="button"
            className={styles.iconButton}
            aria-label={t(`${NS}.steps.moveUp`, { no: stepNo })}
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            <Icon name="chevronDown" size={14} className={styles.flipped} />
          </button>
          <button
            type="button"
            className={styles.iconButton}
            aria-label={t(`${NS}.steps.moveDown`, { no: stepNo })}
            disabled={index === count - 1}
            onClick={() => onMove(1)}
          >
            <Icon name="chevronDown" size={14} />
          </button>
          <button
            type="button"
            className={`${styles.iconButton} ${styles.iconButtonDanger}`}
            aria-label={t(`${NS}.steps.remove`, { no: stepNo })}
            onClick={onRemove}
          >
            <Icon name="close" size={14} />
          </button>
        </div>
      </div>

      <div className={styles.stepGrid}>
        <label className={styles.fieldLabel}>
          {t(`${NS}.steps.approverType`)}
          <Select
            aria-label={t(`${NS}.steps.approverType`)}
            value={step.approverType}
            onChange={(value) => {
              const next = value as ApproverType;
              // Roles and positions are different directories; refs picked from
              // one are meaningless under the other and are cleared, not kept.
              const crossed = (next === 'POSITION') !== (step.approverType === 'POSITION');
              onPatch({ approverType: next, ...(crossed ? { approverRefs: [], quorum: 1 } : {}) });
            }}
            options={APPROVER_TYPES.map((type) => ({
              value: type,
              label: t(`${NS}.approverTypes.${type}`),
            }))}
          />
        </label>

        <div className={styles.fieldLabel}>
          {t(`${NS}.steps.approvers`)}
          <div className={styles.refChips}>
            {step.approverRefs.map((ref) => (
              <span key={ref} className={styles.refChip}>
                {refLabels([ref], options)[0]}
                <button
                  type="button"
                  className={styles.refChipRemove}
                  aria-label={t(`${NS}.steps.removeApprover`, { label: refLabels([ref], options)[0] })}
                  onClick={() => setRefs(step.approverRefs.filter((entry) => entry !== ref))}
                >
                  <Icon name="close" size={11} />
                </button>
              </span>
            ))}
          </div>
          <Select
            aria-label={t(`${NS}.steps.addApprover`)}
            value=""
            searchable
            placeholder={t(`${NS}.steps.addApprover`)}
            onChange={(value) => {
              if (value) setRefs([...step.approverRefs, value]);
            }}
            options={available}
            emptyLabel={t(`${NS}.steps.noMoreApprovers`)}
          />
        </div>

        <div className={styles.fieldLabel}>
          {t(`${NS}.steps.quorum`)}
          <div className={styles.quorumControl}>
            <button
              type="button"
              className={styles.iconButton}
              aria-label={t(`${NS}.steps.quorumDown`, { no: stepNo })}
              disabled={step.quorum <= 1}
              onClick={() => onPatch({ quorum: Math.max(1, step.quorum - 1) })}
            >
              −
            </button>
            <span className={styles.quorumValue} aria-live="polite">
              {step.quorum}
            </span>
            <button
              type="button"
              className={styles.iconButton}
              aria-label={t(`${NS}.steps.quorumUp`, { no: stepNo })}
              disabled={step.quorum >= Math.max(step.approverRefs.length, 1)}
              onClick={() => onPatch({ quorum: Math.min(Math.max(step.approverRefs.length, 1), step.quorum + 1) })}
            >
              +
            </button>
            <span className={styles.quorumHint}>
              {t(`${NS}.steps.quorumHint`, { n: step.approverRefs.length })}
            </span>
          </div>
        </div>

        <label className={styles.fieldLabel}>
          {t(`${NS}.steps.timeout`)}
          <input
            className={styles.textInput}
            type="number"
            min={1}
            value={step.timeoutHours ?? ''}
            placeholder={t(`${NS}.steps.timeoutNone`)}
            onChange={(event) => {
              const raw = event.target.value;
              onPatch({ timeoutHours: raw === '' ? null : Number(raw) });
            }}
          />
        </label>

        {step.timeoutHours !== null && (
          <label className={styles.fieldLabel}>
            {t(`${NS}.steps.escalateTo`)}
            <Select
              aria-label={t(`${NS}.steps.escalateTo`)}
              value={step.escalateTo ?? ''}
              onChange={(value) => onPatch({ escalateTo: value || null })}
              searchable
              options={[{ value: '', label: t(`${NS}.steps.escalateNone`) }, ...options]}
            />
          </label>
        )}

        <div className={styles.selfApproval}>
          <Checkbox
            label={t(`${NS}.steps.allowSelf`)}
            checked={step.allowSelfApproval}
            onChange={(event) => onPatch({ allowSelfApproval: event.target.checked })}
          />
        </div>
      </div>
    </Card>
  );
}

/* ─── The rules panel ─── */

function RulesPanel({
  editor,
  onPatch,
}: {
  editor: EditorState;
  onPatch: (patch: Partial<EditorState>) => void;
}) {
  const { t } = useTranslation();

  const patchCondition = (patch: Partial<ConditionDraft>) =>
    onPatch({ condition: { ...editor.condition, ...patch } });

  return (
    <section aria-label={t(`${NS}.rules.title`)}>
      <h2 className={styles.sectionTitle}>{t(`${NS}.rules.title`)}</h2>
      <Card className={styles.rulesCard}>
        <div className={styles.ruleRow}>
          <Checkbox
            label={t(`${NS}.rules.initiator`)}
            checked={editor.initiatorCannotApprove}
            onChange={(event) => onPatch({ initiatorCannotApprove: event.target.checked })}
          />
          <p className={styles.ruleHint}>{t(`${NS}.rules.initiatorHint`)}</p>
        </div>

        <div className={styles.ruleRow}>
          <Checkbox
            label={t(`${NS}.rules.distinct`)}
            checked={editor.distinctApproversAcrossSteps}
            onChange={(event) => onPatch({ distinctApproversAcrossSteps: event.target.checked })}
          />
          <p className={styles.ruleHint}>{t(`${NS}.rules.distinctHint`)}</p>
        </div>

        <div className={styles.ruleRow}>
          <Checkbox
            label={t(`${NS}.rules.conditionToggle`)}
            checked={editor.condition.enabled}
            onChange={(event) => patchCondition({ enabled: event.target.checked })}
          />
          <p className={styles.ruleHint}>{t(`${NS}.rules.conditionHint`)}</p>
          {editor.condition.enabled && (
            <div className={styles.conditionRow}>
              <label className={styles.fieldLabel}>
                {t(`${NS}.rules.conditionField`)}
                <input
                  className={styles.textInput}
                  value={editor.condition.field}
                  placeholder={t(`${NS}.rules.conditionFieldPlaceholder`)}
                  onChange={(event) => patchCondition({ field: event.target.value })}
                />
              </label>
              <label className={styles.fieldLabel}>
                {t(`${NS}.rules.conditionOp`)}
                <Select
                  aria-label={t(`${NS}.rules.conditionOp`)}
                  value={editor.condition.op}
                  onChange={(value) => patchCondition({ op: value as ConditionOp })}
                  options={CONDITION_OPS.map((op) => ({
                    value: op,
                    label: t(`${NS}.conditionOps.${op}`),
                  }))}
                />
              </label>
              <label className={styles.fieldLabel}>
                {t(`${NS}.rules.conditionValue`)}
                <input
                  className={styles.textInput}
                  value={editor.condition.value}
                  onChange={(event) => patchCondition({ value: event.target.value })}
                />
                {editor.condition.op === 'IN' && (
                  <span className={styles.ruleHint}>{t(`${NS}.rules.conditionValueHint`)}</span>
                )}
              </label>
            </div>
          )}
        </div>
      </Card>
    </section>
  );
}

/* ─── A version that is law or history: shown, not edited ─── */

function ReadOnlyDefinition({ definition, locale }: { definition: WorkflowDefinition; locale: string }) {
  const { t } = useTranslation();

  return (
    <Card className={styles.readOnlyCard}>
      <p className={styles.readOnlyNotice}>
        <Icon name="info" size={15} />
        {t(`${NS}.designer.readOnly`, {
          status: t(`platform.state.lifecycle.${definition.status}`, { defaultValue: definition.status }),
        })}
      </p>
      <dl className={styles.detailFactsWide}>
        <div>
          <dt>{t(`${NS}.designer.titleLabel`)}</dt>
          <dd>{definition.title}</dd>
        </div>
        <div>
          <dt>{t(`${NS}.designer.versionLabel`, { version: definition.version })}</dt>
          <dd>{formatDateTime(definition.createdAt, locale)}</dd>
        </div>
        <div>
          <dt>{t(`${NS}.rules.initiator`)}</dt>
          <dd>{t(definition.rules.initiatorCannotApprove ? 'common.yes' : 'common.no')}</dd>
        </div>
        <div>
          <dt>{t(`${NS}.rules.distinct`)}</dt>
          <dd>{t(definition.rules.distinctApproversAcrossSteps ? 'common.yes' : 'common.no')}</dd>
        </div>
        {definition.rules.condition && (
          <div>
            <dt>{t(`${NS}.rules.conditionToggle`)}</dt>
            <dd>
              <code className={styles.conditionCode}>
                {definition.rules.condition.field} {definition.rules.condition.op}{' '}
                {Array.isArray(definition.rules.condition.value)
                  ? definition.rules.condition.value.join(', ')
                  : String(definition.rules.condition.value)}
              </code>
            </dd>
          </div>
        )}
      </dl>
      <ol className={styles.readOnlySteps}>
        {definition.steps.map((step) => (
          <li key={step.stepNo}>
            <span className={styles.readOnlyStepTitle}>
              {step.title || t(`${NS}.chain.untitledStep`)}
            </span>
            <span className={styles.readOnlyStepWho}>
              {summarizeApprovers(step, [], t)}
              {step.timeoutHours !== null &&
                ` · ${t(`${NS}.steps.timeoutSummary`, { hours: step.timeoutHours })}`}
            </span>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/* ─── The append-only history feed ─── */

function HistoryModal({
  definition,
  institutionId,
  locale,
  onClose,
}: {
  definition: WorkflowDefinition | null;
  institutionId: string;
  locale: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const { state, reload } = useAsyncData<WorkflowHistoryEvent[]>(
    `wf-history-${institutionId}-${definition?.id ?? 'none'}`,
    () => workflowsApi.admin.history(institutionId, definition?.id ?? ''),
    definition !== null,
  );

  if (!definition) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={t(`${NS}.designer.historyTitle`)}
      description={t(`${NS}.designer.versionLabel`, { version: definition.version })}
    >
      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t(`${NS}.designer.historyEmpty`)}
      >
        {(events) => (
          <ul className={styles.historyList}>
            {events.map((event) => (
              <li key={event.id} className={styles.historyRow}>
                <span className={styles.historyType}>{event.type}</span>
                <span className={styles.historyMeta}>
                  {event.actor ? `${event.actor} · ` : ''}
                  {formatDateTime(event.at, locale)}
                </span>
                {event.detail && <p className={styles.historyDetail}>{event.detail}</p>}
              </li>
            ))}
          </ul>
        )}
      </AsyncPanel>
    </Modal>
  );
}
